import React, {
  Children,
  cloneElement,
  Fragment,
  ReactElement,
  ReactHTMLElement,
  ReactNode,
} from 'react';

import cs from 'classnames';
import styled from 'styled-components';

import {FormErrorsProps, withFormErrors} from './with-form-errors';

const fieldTypes = ['input', 'textarea', 'select', 'div'];

interface Props extends FormErrorsProps {
  className?: string;
  field: string | Array<string>;
  children:
    | ReactElement<any>
    | Array<ReactElement<any> | null>
    | ((
        invalidFeedback: ReactElement<'div'> | null,
        errorClass: string,
        hasErrors: boolean
      ) => ReactElement<any> | Array<ReactElement<any>>);
}

export const FormControlGroup = withFormErrors(function FormControlGroup({
  children,
  field,
  formErrors,
  className,
  ...other
}: Props) {
  const errors = React.useMemo(() => {
    const fields = Array.isArray(field) ? field : [field];

    return fields.reduce(
      (errors, field) => errors.concat(formErrors[field] ?? []),
      [] as Array<string>
    );
  }, [field, formErrors]);

  const invalidFeedbackElement = React.useMemo(() => {
    return errors.length === 0 ? null : (
      <div className="invalid-feedback">
        {errors.map((message, idx) => (
          <span key={idx}>{message}</span>
        ))}
      </div>
    );
  }, [errors]);

  const {hasErrors, errorClass} = React.useMemo(() => {
    return {
      hasErrors: errors.length > 0,
      errorClass: errors.length > 0 ? 'is-invalid' : '',
    };
  }, [errors.length]);

  return (
    <StyledFieldContainerDiv {...other} className={cs('form-group', className)}>
      {typeof children === 'function' ? (
        children(invalidFeedbackElement, errorClass, hasErrors)
      ) : (
        <Fragment>
          {Children.toArray(children)
            .map(identifyInputElements)
            .map(({element, canAddClass}) =>
              canAddClass
                ? cloneElement(element, {
                    className: `${element.props.className || ''} ${errorClass}`,
                  })
                : element
            )}
          {invalidFeedbackElement}
        </Fragment>
      )}
    </StyledFieldContainerDiv>
  );
});

function identifyInputElements(
  element: Exclude<ReactNode, boolean | null | undefined>
):
  | {
      canAddClass: false;
      element: Exclude<ReactNode, boolean | null | undefined>;
    }
  | {
      canAddClass: true;
      element: ReactHTMLElement<any>;
    } {
  if (typeof element == 'string' || typeof element == 'number') {
    return {canAddClass: false, element};
  }

  return 'type' in element &&
    typeof element.type === 'string' &&
    fieldTypes.indexOf(element.type) > -1
    ? {
        element: element as ReactHTMLElement<any>,
        canAddClass: true,
      }
    : {canAddClass: false, element};
}

const StyledFieldContainerDiv = styled.div`
  .invalid-feedback {
    display: block;
  }

  .input-group-text.bg-white.is-invalid {
    border-color: #dc3545;
  }
`;
